import { useAuth, useDocumentTitle, usePrevious } from '@/hooks';
import { Router as BoxIcon } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  CardActions,
  CardHeader,
  Divider,
  ListItem,
  ListSubheader,
  Skeleton,
  Tab,
  Tabs,
  Toolbar,
} from '@mui/material';
import { isValid, subDays } from 'date-fns';
import _ from 'lodash';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
// import {
//   Field,
//   // TextField,
//   // required,
//   SelectField,
//   CheckboxField,
// } from '@/components/fields';
import {
  DELETE_TELEMATICS_BOX,
  END_TELEMATICS_BOX_POLLS_STREAM,
  FETCH_TELEMATICS_BOX_POLLS,
  START_TELEMATICS_BOX_POLLS_STREAM,
  UPDATE_TELEMATICS_BOX,
  UPDATE_VEHICLE_IMEI,
} from '@/actions';
import { DioTable } from './DioTable';
import { PollTable } from './PollTable';
import { TelematicsBoxVehicles } from './TelematicsBoxVehicles';
// import { vinErrors } from './utilities';
import { Section, TelematicsBoxMap } from '@/components/controls';
import { ConfirmationDialog } from '@/components/dialogs';
import { noop } from '@/utils';
import { dioOptions } from '@/utils/config';
import { useCommissioningDialog } from './CommissioningDialog';
import { DriverIdStatus } from './DriverIDSection';
import { TrackingStatus } from './TrackingStateControl';
import { useLatestPoll } from './useLatestPoll';

const { hideMap, showDriverId, showTracking } = dioOptions;

// const composeValidators = (...validators) => (value) =>
//   validators.reduce((error, validator) => error || validator(value), undefined);

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index ? children : <Fragment />}
    </Box>
  );
}

export function TelematicsBox() {
  const { id } = useParams();
  useDocumentTitle(`IR3 | Telematics Box | ${id}`);
  const auth = useAuth();
  const dispatch = useDispatch();
  const latestPoll = useLatestPoll(id);

  const boxesByImei = useSelector((state) => state.telematicsBoxes.boxesByImei);
  const box = boxesByImei[id];
  const { imei, polls, mostRecentTime } = box || {};
  const ordered = _.orderBy(polls || [], ['time'], ['desc']); // sort by time

  const [currentTab, setCurrentTab] = useState(0);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const commissioningDialog = useCommissioningDialog();

  const formRef = useRef();

  const socketIsSubscribed = useSelector(
    (state) => state.telematicsBoxes.socketIsSubscribed,
  );

  // wipe state when different box
  const prevId = usePrevious(id);
  useEffect(() => {
    if (prevId !== id && formRef.current) {
      formRef.current.restart();
      formRef.current.initialize({
        identificationNumber: box?.identificationNumber,
        fleetNumber: box?.fleetNumber,
      });
    }
  }, [prevId, id, formRef, box]);

  // startup (only times when id, selectedXXX change)
  useEffect(() => {
    if (imei && !socketIsSubscribed) {
      let lastTime = new Date(mostRecentTime);

      //console.log("Going to listen for " + imei + " around " + format(lastTime));
      // if there is a last response time, and it's not from today, query from then
      if (!isValid(lastTime) || lastTime > subDays(new Date(), 1)) {
        lastTime = new Date();
      }

      let start = subDays(lastTime, 1);
      let end = new Date(lastTime);

      dispatch({
        type: FETCH_TELEMATICS_BOX_POLLS,
        payload: {
          imei,
          start,
          end,
        },
      });

      dispatch({
        type: START_TELEMATICS_BOX_POLLS_STREAM,
        payload: { imei },
      });
    }

    // shutdown
    return () => {
      if (socketIsSubscribed) {
        dispatch({
          type: END_TELEMATICS_BOX_POLLS_STREAM,
        });
      }
    };
  }, [imei, mostRecentTime, socketIsSubscribed, dispatch]);

  function onSubmit(box) {
    dispatch({
      type: UPDATE_TELEMATICS_BOX,
      payload: {
        imei,
        ...box,
      },
    });
  }

  function handleChangeTab(event, value) {
    setCurrentTab(value);
  }

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  function renderSkeleton() {
    return (
      <Toolbar color="primary" disableGutters style={{ paddingRight: 12 }}>
        <CardHeader
          avatar={
            <Avatar>
              {<Skeleton variant="circular" width="100%" height="100%" />}
            </Avatar>
          }
          title={<Skeleton height={10} width="150px" />}
        />
      </Toolbar>
    );
  }

  function renderNotFound() {
    return (
      <Toolbar color="primary" disableGutters sx={{ pr: 3 }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: 'error.main' }}>
              <BoxIcon />
            </Avatar>
          }
          title={`IMEI ${id} not found`}
        />
      </Toolbar>
    );
  }

  function deleteImei() {
    if (box.identificationNumber) {
      dispatch({
        type: UPDATE_VEHICLE_IMEI,
        payload: {
          vehicle: { identificationNumber: box.identificationNumber },
          telematicsBoxImei: '',
        },
      });
    }

    dispatch({
      type: DELETE_TELEMATICS_BOX,
      payload: imei,
      navigate: noop,
    });
  }

  const canEdit = auth.getAuthorisation()?.telematicsBoxes?.write;
  const canCommission =
    auth.getAuthorisation()?.commissioning?.write &&
    auth.getAuthorisation()?.vehicles?.write;

  function renderBoxDetails() {
    return (
      <Form
        onSubmit={onSubmit}
        // debug={console.log}
        initialValues={box} // should reset the form when the id changes
        render={({ handleSubmit, form }) => {
          formRef.current = form;
          // const buttonsDisabled = pristine || submitting;

          return (
            <form onSubmit={handleSubmit}>
              <CardHeader
                avatar={
                  <Avatar>
                    <BoxIcon />
                  </Avatar>
                }
                action={
                  canCommission && (
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={() => {
                        commissioningDialog.show({ box });
                      }}
                      // sx={{ width: 150, mr: 1 }}
                    >
                      Commission
                    </Button>
                  )
                }
                title={id}
              />
              <Box>
                <Box sx={{ padding: 0 }}>
                  <Tabs
                    value={currentTab}
                    onChange={handleChangeTab}
                    indicatorColor="primary"
                    //textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab {...a11yProps(0)} label="Info" />
                    <Tab {...a11yProps(1, true)} label="Polls" />
                    <Tab {...a11yProps(2, true)} label="Events" />
                  </Tabs>
                  <TabPanel value={currentTab} index={0}>
                    {/* <Prompt when={dirty} message={"Unsaved changes will be lost, are you sure?"} /> */}
                    {/* <ListSubheader disableSticky>
                          Telematics Box
                        </ListSubheader>
                        <ListItem>
                          <Field
                            name="make"
                            className={classes.field}
                            label="Make"
                            component={SelectField}
                            values={[
                              { label: 'Huf', value: 'huf' },
                              { label: 'Astra', value: 'astra' },
                            ]}
                            disabled={!canEdit}
                          />
                          <Field
                            name="model"
                            className={classes.field}
                            label="Model"
                            component={SelectField}
                            values={[
                              { label: 'A', value: 'a' },
                              { label: 'B', value: 'b' },
                            ]}
                            disabled={!canEdit}
                          />
                          <Field
                            name="hardwareVersion"
                            className={classes.field}
                            label="Hardware Version"
                            component={SelectField}
                            values={[
                              { label: '1', value: '1' },
                              { label: '2', value: '2' },
                            ]}
                            disabled={!canEdit}
                          />
                        </ListItem>
                        <ListItem>
                          <Field
                            name="active"
                            className={classes.field}
                            label="Active"
                            component={CheckboxField}
                            disabled={!canEdit}
                          />
                        </ListItem>
                        <Divider /> */}
                    {showDriverId && (
                      <Fragment>
                        <ListSubheader disableSticky>Driver ID</ListSubheader>
                        <Section>
                          <DriverIdStatus imei={box?.imei} />
                        </Section>
                        <Divider />
                      </Fragment>
                    )}
                    {showTracking && (
                      <Fragment>
                        <ListSubheader disableSticky>Tracking</ListSubheader>
                        <Section>
                          <TrackingStatus imei={box?.imei} />
                        </Section>
                        <Divider />
                      </Fragment>
                    )}
                    <ListSubheader disableSticky>Status</ListSubheader>
                    <Section>
                      <Box sx={{ width: 1, maxWidth: 1024 }}>
                        <PollTable polls={ordered} pollsToShow={1} />
                      </Box>
                    </Section>
                    <Divider />
                    <ListSubheader disableSticky>
                      Last State Change
                    </ListSubheader>
                    <Section>
                      <Box sx={{ width: 1, maxWidth: 1024 }}>
                        <DioTable
                          polls={ordered}
                          pollsToShow={1}
                          // showTime={false}
                        />
                      </Box>
                    </Section>
                    <Divider />
                    {!hideMap && (
                      <Fragment>
                        <ListSubheader disableSticky>
                          Last known location
                        </ListSubheader>
                        <Box
                          sx={{
                            height: latestPoll?.position ? 500 : undefined,
                            p: latestPoll?.position ? 0 : 2,
                          }}
                        >
                          {latestPoll?.position ? (
                            <TelematicsBoxMap point={latestPoll.position} />
                          ) : (
                            'No location received'
                          )}
                        </Box>
                        <Divider />
                      </Fragment>
                    )}
                    <ListSubheader disableSticky>Vehicle</ListSubheader>
                    {/* <ListItem>
                          <Field
                            name="identificationNumber"
                            className={classes.field}
                            disabled={!!box?.identificationNumber}
                            // initialValue={box?.identificationNumber}
                            label="VIN"
                            validate={composeValidators(required, vinErrors)}
                          >
                            {(props) => (
                              <TextField
                                {...props}
                                value={
                                  box?.identificationNumber || props.input.value
                                }
                                inputProps={{
                                  className: classes.vinText,
                                  spellCheck: 'false',
                                }}
                              />
                            )}
                          </Field>
                          <Field
                            name="fleetNumber"
                            disabled={!!box?.fleetNumber}
                            value={box?.fleetNumber}
                            className={classes.field}
                            label="Fleet Number"
                          >
                            {(props) => (
                              <TextField
                                {...props}
                                value={box?.fleetNumber || props.input.value}
                                inputProps={{
                                  className: classes.vinText,
                                  spellCheck: 'false',
                                }}
                              />
                            )}
                          </Field>
                        </ListItem> */}
                    <ListItem>
                      <TelematicsBoxVehicles imei={id} />
                    </ListItem>
                    <Divider />
                    {/* <ListItem>
                        <Field
                          name="odometer"
                          className={classes.field}
                          label="Current Odometer Reading"
                          component={TextField}
                        />
                      </ListItem> */}
                  </TabPanel>
                  <TabPanel value={currentTab} index={1} sx={{ p: 1 }}>
                    <PollTable polls={ordered} pollsToShow={20} />
                  </TabPanel>
                  <TabPanel value={currentTab} index={2} sx={{ p: 1 }}>
                    <DioTable polls={ordered} pollsToShow={20} />
                  </TabPanel>
                </Box>
              </Box>
              {currentTab === 0 && canEdit && (
                <CardActions>
                  {/* <Button
                      type="submit"
                      className={classes.positiveButton}
                      disabled={buttonsDisabled}
                    >
                      Save
                    </Button>
                    <Button
                      className={classes.negativeButton}
                      disabled={buttonsDisabled}
                      onClick={restart}
                    >
                      Cancel
                    </Button> */}
                  <Button
                    color="error"
                    onClick={() => setShowConfirmDelete(true)}
                  >
                    Delete
                  </Button>
                </CardActions>
              )}
            </form>
          );
        }}
      />
    );
  }

  function renderContents() {
    if (!box) {
      if (Object.keys(boxesByImei).length > 0) {
        return renderNotFound();
      } else {
        return renderSkeleton();
      }
    } else {
      return renderBoxDetails();
    }
  }

  return (
    <Box sx={{ flexGrow: 1, p: 1, bgcolor: 'background.paper' }}>
      {renderContents()}
      <ConfirmationDialog
        action="Delete"
        open={showConfirmDelete}
        itemId={imei}
        onOk={deleteImei}
        onCancel={() => setShowConfirmDelete(false)}
      />
    </Box>
  );
}
